import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { claimComplete, claimOpen, type ClaimOpenResult, type CommerceSession } from '@services/partnerService';
import { Button, Card, ErrorNote, Field, Muted, PageShell, Title } from '../ui';

// Promueve el draft a sesion plena del comercio: escribe localStorage (lo que lee checkAuth del
// panel) y recarga al panel ya logueado. NO usa el AuthContext del panel (app separada).
const persistSessionAndGoToPanel = (s: CommerceSession) => {
  localStorage.setItem('id_commerce', s.id_commerce);
  localStorage.setItem('id_user', s.id_user);
  localStorage.setItem('id_path', s.id_path);
  localStorage.setItem('commerce_name', s.commerce_name);
  localStorage.setItem('commerce_logo', s.commerce_logo || '');
  localStorage.setItem('user_name', s.user_name);
  localStorage.setItem('token', s.token);
  localStorage.setItem('userData', JSON.stringify(s.user));
  if (s.onboarding_finish_date) localStorage.setItem('onboarding_finish_date', s.onboarding_finish_date);
  if (s.user_type) localStorage.setItem('user_type', s.user_type);
  localStorage.setItem('is_root', String(Boolean(s.is_root)));
  window.location.assign('/');
};

const ClaimPage = () => {
  const { token = '' } = useParams<{ token: string }>();
  const [data, setData] = useState<ClaimOpenResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [otp, setOtp] = useState('');
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');
  const [nombre, setNombre] = useState('');
  const [correo, setCorreo] = useState('');
  const [terms, setTerms] = useState(false);

  const open = useCallback(async () => {
    setLoading(true);
    try {
      const res = await claimOpen(token);
      setData(res);
      if (res.negocio?.nombre_contacto) setNombre(res.negocio.nombre_contacto);
      if (res.negocio?.correo_contacto) setCorreo(res.negocio.correo_contacto);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setInvalid(status === 410 ? 'Este enlace ya no es valido o expiro.' : 'No encontramos este enlace.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    open();
  }, [open]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!data) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await claimComplete({
        token,
        phone: data.phone || '',
        otp_code: otp.trim(),
        usuario: usuario.trim(),
        password,
        nombre: nombre.trim(),
        correo: correo.trim(),
        acepta_terminos: terms,
      });
      persistSessionAndGoToPanel(res.session);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 400) setError('El codigo de verificacion es invalido.');
      else if (status === 410) setError('El enlace ya fue utilizado.');
      else setError('No se pudo completar el registro. Intenta de nuevo.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageShell narrow>
        <Card>
          <Title>Cargando...</Title>
        </Card>
      </PageShell>
    );
  }

  if (invalid) {
    return (
      <PageShell narrow>
        <Card>
          <Title>Enlace no disponible</Title>
          <ErrorNote>{invalid}</ErrorNote>
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell narrow>
      <Card>
        <Title
          sub={
            data?.negocio?.nombre
              ? `Reclama "${data.negocio.nombre}" y crea tu cuenta.`
              : 'Reclama tu comercio y crea tu cuenta.'
          }
        >
          Completa tu registro
        </Title>
        {data?.menu_preview?.categorias?.length ? (
          <Muted style={{ color: '#037E00' }}>
            Tu menu ya tiene {data.menu_preview.categorias.length} categoria(s) cargada(s).
          </Muted>
        ) : null}
        <Muted>
          {data?.otp_sent && data?.phone
            ? `Te enviamos un codigo por WhatsApp a ${data.phone}.`
            : 'Ingresa el codigo de verificacion que recibiste.'}
        </Muted>
        <form onSubmit={onSubmit} style={{ marginTop: 12 }}>
          <Field
            label="Codigo de verificacion"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            inputMode="numeric"
            required
          />
          <Field label="Tu nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
          <Field label="Correo" type="email" value={correo} onChange={(e) => setCorreo(e.target.value)} required />
          <Field
            label="Usuario"
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
            autoComplete="username"
            required
          />
          <Field
            label="Contrasena"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
          <label
            style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: '#6B6B6B', marginTop: 4 }}
          >
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} required />
            <span>Acepto los terminos y condiciones.</span>
          </label>
          {error && <ErrorNote>{error}</ErrorNote>}
          <Button type="submit" fullWidth disabled={submitting} style={{ marginTop: 18 }}>
            {submitting ? 'Creando tu cuenta...' : 'Crear mi cuenta'}
          </Button>
        </form>
      </Card>
    </PageShell>
  );
};

export default ClaimPage;
